import { useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { Check } from 'lucide-react'

export interface WorkflowStage {
    label: string
    note: string
}

export const WORKFLOW_STAGES: WorkflowStage[] = [
    { label: 'Submitted', note: 'The manuscript is submitted through the editorial portal with its files and cover letter.' },
    { label: 'Received', note: 'The editorial office acknowledges receipt and logs the manuscript for screening.' },
    { label: 'Sent to Reviewer', note: 'After desk screening, the paper is assigned to independent double-blind reviewers.' },
    { label: 'Under Review', note: 'Reviewers evaluate the manuscript against the journal’s scope and quality criteria.' },
    { label: 'Reviewer Feedback', note: 'Completed reviewer reports are returned to the editorial team for consideration.' },
    { label: 'Revision', note: 'The author revises the manuscript in response to the reviewer and editor comments.' },
    { label: 'Accepted / Rejected', note: 'The Editor-in-Chief communicates the final editorial decision to the author.' },
    { label: 'Declaration Form', note: 'The author files the declaration covering the processing fee, plagiarism and ethical-AI undertakings, copyright transfer, competing interests and funding.' },
    { label: 'Pending Fee', note: 'The Article Processing Charge invoice is issued to the corresponding author.' },
    { label: 'Payment Received', note: 'The APC payment is confirmed and the article moves into production.' },
    { label: 'Published', note: 'The accepted paper appears in the next open-access issue and is publicly available.' }
]

export default function WorkflowStages({
    stages = WORKFLOW_STAGES,
    showNotes = true
}: {
    stages?: WorkflowStage[]
    showNotes?: boolean
}) {
    const reduced = useReducedMotion()
    const [active, setActive] = useState(0)
    const [paused, setPaused] = useState(false)
    const scrollerRef = useRef<HTMLDivElement>(null)
    const nodeRefs = useRef<(HTMLLIElement | null)[]>([])

    const n = stages.length
    const edge = 50 / n

    useEffect(() => {
        if (reduced || paused) return
        const id = setInterval(() => setActive(a => (a + 1) % n), 2400)
        return () => clearInterval(id)
    }, [reduced, paused, n])

    useEffect(() => {
        const scroller = scrollerRef.current
        const node = nodeRefs.current[active]
        if (!scroller || !node || scroller.scrollWidth <= scroller.clientWidth) return
        const sRect = scroller.getBoundingClientRect()
        const nRect = node.getBoundingClientRect()
        scroller.scrollBy({ left: nRect.left + nRect.width / 2 - (sRect.left + sRect.width / 2), behavior: 'smooth' })
    }, [active])

    return (
        <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
            <div ref={scrollerRef} className="overflow-x-auto pb-3 -mx-4 px-4 sm:mx-0 sm:px-0">
                <div className="relative" style={{ minWidth: `${n * 5.5}rem` }}>
                    <div
                        aria-hidden
                        className="absolute top-[1.1rem] h-px"
                        style={{ left: `${edge}%`, right: `${edge}%`, background: 'var(--color-rule)' }}
                    >
                        <div
                            className="absolute left-0 top-0 h-px"
                            style={{
                                width: `${(active / (n - 1)) * 100}%`,
                                background: 'var(--color-gold-500)',
                                transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        />
                    </div>
                    <ol className="flex items-start">
                        {stages.map((s, i) => {
                            const reached = reduced || i <= active
                            const isActive = i === active
                            return (
                                <li
                                    key={s.label}
                                    ref={el => {
                                        nodeRefs.current[i] = el
                                    }}
                                    className="flex-1 flex flex-col items-center text-center relative"
                                >
                                    <motion.button
                                        type="button"
                                        onClick={() => setActive(i)}
                                        aria-label={`Stage ${i + 1}: ${s.label}`}
                                        aria-current={isActive ? 'step' : undefined}
                                        initial={{ opacity: 0, scale: 0.6 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.35, delay: i * 0.04 }}
                                        whileHover={{ scale: 1.12 }}
                                        className="relative z-[1] inline-flex items-center justify-center font-display cursor-pointer"
                                        style={{
                                            width: '2.2rem',
                                            height: '2.2rem',
                                            borderRadius: '50%',
                                            background: reached ? 'var(--color-gold-500)' : 'var(--color-paper-50)',
                                            color: reached ? 'var(--color-paper-50)' : 'var(--color-ink-300)',
                                            border: `1px solid ${reached ? 'var(--color-gold-600)' : 'var(--color-rule-strong)'}`,
                                            fontSize: '0.85rem',
                                            boxShadow: reached ? '0 4px 14px -8px rgba(158,43,37,0.65)' : 'none',
                                            transition: 'background 0.4s, color 0.4s, border-color 0.4s'
                                        }}
                                    >
                                        {isActive && !reduced && (
                                            <motion.span
                                                aria-hidden
                                                className="absolute inset-0 rounded-full"
                                                style={{ border: '1.5px solid var(--color-gold-500)' }}
                                                animate={{ scale: [1, 1.55], opacity: [0.75, 0] }}
                                                transition={{ repeat: Infinity, duration: 1.4, ease: 'easeOut' }}
                                            />
                                        )}
                                        {i === n - 1 ? <Check size={15} strokeWidth={2.5} /> : i + 1}
                                    </motion.button>
                                    <span
                                        className="mt-2.5 px-1 text-[9.5px] sm:text-[10px] uppercase font-semibold leading-tight"
                                        style={{
                                            letterSpacing: '0.08em',
                                            color: isActive ? 'var(--color-gold-700)' : 'var(--color-ink-600)',
                                            transition: 'color 0.4s'
                                        }}
                                    >
                                        {s.label}
                                    </span>
                                </li>
                            )
                        })}
                    </ol>
                </div>
            </div>

            {showNotes && (
                <div
                    className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px"
                    style={{ background: 'var(--color-rule)', border: '1px solid var(--color-rule)' }}
                >
                    {stages.map((s, i) => {
                        const isActive = i === active
                        return (
                            <motion.div
                                key={s.label}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
                                onClick={() => setActive(i)}
                                className="relative p-5 cursor-pointer"
                                style={{
                                    background: isActive ? 'var(--color-paper-100)' : 'var(--color-paper-50)',
                                    transition: 'background 0.4s'
                                }}
                            >
                                <span
                                    aria-hidden
                                    className="absolute left-0 top-0 bottom-0 w-[3px]"
                                    style={{
                                        background: 'var(--color-gold-500)',
                                        transform: isActive ? 'scaleY(1)' : 'scaleY(0)',
                                        transformOrigin: 'top',
                                        transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                />
                                <div className="flex items-baseline gap-3">
                                    <span
                                        className="font-display numerals"
                                        style={{
                                            fontSize: '0.95rem',
                                            color: isActive ? 'var(--color-gold-500)' : 'var(--color-gold-700)',
                                            letterSpacing: '0.03em'
                                        }}
                                    >
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                    <span
                                        className="font-display"
                                        style={{ fontSize: '1.02rem', color: 'var(--color-ink-700)', letterSpacing: '-0.01em', fontWeight: 500 }}
                                    >
                                        {s.label}
                                    </span>
                                </div>
                                <p className="mt-2 text-[13.5px] leading-relaxed" style={{ color: 'var(--color-ink-400)' }}>
                                    {s.note}
                                </p>
                            </motion.div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
